/**
 * The map sheet — the whole job, full height, over a veil (D-028).
 *
 * A dialog: `role="dialog" aria-modal="true"`, focus moves to the close
 * button on open and returns to the map bar on close, Escape closes, the veil
 * closes, a swipe down on the sheet head closes. It is the first true overlay
 * in the app — everything before it (the confirm panel, the warn boxes) was
 * laid into the page — so the mechanics are kept in one place and small.
 *
 * Presentational apart from focus: the tree it renders is `MapTreeView`, the
 * one renderer the `sections` landing page uses too.
 */

import { useEffect, useRef, type ReactNode } from 'react';

import type { MapModel, MapRowModel } from '../state/mapTree';
import { MapTreeView } from './MapTreeView';

export interface MapSheetProps {
  model: MapModel;
  open: boolean;
  born: boolean;
  openBranches: ReadonlySet<string>;
  onToggleBranch: (roomId: string) => void;
  onClose: () => void;
  onNavigate: (row: MapRowModel) => void;
  /** Where focus goes back to on close — the map bar. */
  returnFocusTo?: React.RefObject<HTMLElement | null>;
}

export function MapSheet({
  model,
  open,
  born,
  openBranches,
  onToggleBranch,
  onClose,
  onNavigate,
  returnFocusTo,
}: MapSheetProps): ReactNode {
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const touchY = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      returnFocusTo?.current?.focus();
    };
  }, [open, onClose, returnFocusTo]);

  return (
    <>
      <div
        className={open ? 'veil veil--on' : 'veil'}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={open ? 'sheet sheet--open' : 'sheet'}
        role="dialog"
        aria-modal="true"
        aria-label="Survey map"
        aria-hidden={!open}
      >
        <div
          className="sheet__head"
          onTouchStart={(event) => {
            touchY.current = event.touches[0]?.clientY ?? null;
          }}
          onTouchMove={(event) => {
            const y = event.touches[0]?.clientY;
            if (touchY.current !== null && y !== undefined && y - touchY.current > 30) {
              touchY.current = null;
              onClose();
            }
          }}
        >
          <span className="sheet__grab" aria-hidden="true" />
          <div className="sheet__title" data-legibility="display">
            {model.surveyTitle}
          </div>
          <button
            type="button"
            className="sheet__close"
            aria-label="Close map"
            onClick={onClose}
            ref={closeButton}
            tabIndex={open ? 0 : -1}
          >
            ✕
          </button>
        </div>
        <div className="sheet__tree">
          <MapTreeView
            model={model}
            openBranches={openBranches}
            onToggleBranch={onToggleBranch}
            onNavigate={onNavigate}
            born={born}
          />
        </div>
      </div>
    </>
  );
}
